import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listIncidents } from "@/lib/incidents";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import {
  DEMO_CHILDREN,
  DEMO_CLASSROOMS,
} from "@/lib/dailyReports";
import {
  SEVERITIES,
  STATUSES,
  formatIncidentDateTime,
} from "@/lib/incidentTypes";
import {
  IncidentTypeBadge,
  SeverityBadge,
  StatusBadge,
} from "@/components/incidents/IncidentBadges";
import { getChildById } from "@/lib/dailyReportsDemo";

export default function AdminIncidents() {
  const { profile, role } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classrooms, setClassrooms] = useState(DEMO_CLASSROOMS);
  const [childrenList, setChildrenList] = useState(DEMO_CHILDREN);

  const [filters, setFilters] = useState({
    classroomId: "",
    childId: "",
    severity: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    (async () => {
      const [cRes, kRes] = await Promise.all([
        supabase.from("classrooms").select("id, name").order("name"),
        supabase
          .from("children")
          .select("id, first_name, last_name, classroom_id")
          .order("first_name"),
      ]);
      if (cRes.data) setClassrooms(cRes.data);
      if (kRes.data) setChildrenList(kRes.data);
    })();
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      setLoading(true);
      try {
        const data = await listIncidents({
          role,
          profileId: profile.id,
          filters: {
            classroomId: filters.classroomId || undefined,
            childId: filters.childId || undefined,
            severity: filters.severity || undefined,
            status: filters.status || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
          },
        });
        setRows(data);
      } catch (err) {
        toast.error(err.message || "Could not load incidents");
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.id, role, filters]);

  const filteredChildren = useMemo(() => {
    if (!filters.classroomId) return childrenList;
    return childrenList.filter((c) => c.classroom_id === filters.classroomId);
  }, [childrenList, filters.classroomId]);

  const totals = useMemo(() => {
    const all = rows.length;
    const highSev = rows.filter((r) => r.severity === "high").length;
    const unacked = rows.filter((r) => !r.acknowledged_at).length;
    return { all, highSev, unacked };
  }, [rows]);

  return (
    <div data-testid="admin-incidents" className="space-y-8">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Operational
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Incident reports
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Review every incident filed across the organization. Teacher
              attribution and parent acknowledgement are preserved on each row.
            </p>
          </div>
          <Link
            to="/admin/incidents/new"
            data-testid="admin-incidents-new"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            New incident
          </Link>
        </div>
      </section>

      {/* KPI strip */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testId="kpi-total" label="Total" value={String(totals.all)} />
        <Kpi testId="kpi-high" label="High severity" value={String(totals.highSev)} accent />
        <Kpi testId="kpi-unacked" label="Awaiting parent ack" value={String(totals.unacked)} />
      </section>

      {/* Filters */}
      <section
        data-testid="incidents-filters"
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        <div className="mb-4 flex items-center gap-2">
          <Funnel size={18} weight="duotone" className="text-primary-hover" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Filters
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <FilterSelect
            testId="filter-classroom"
            label="Classroom"
            value={filters.classroomId}
            onChange={(v) => setFilters((f) => ({ ...f, classroomId: v, childId: "" }))}
            options={[
              { value: "", label: "All classrooms" },
              ...classrooms.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <FilterSelect
            testId="filter-child"
            label="Child"
            value={filters.childId}
            onChange={(v) => setFilters((f) => ({ ...f, childId: v }))}
            options={[
              { value: "", label: "All children" },
              ...filteredChildren.map((c) => ({
                value: c.id,
                label: `${c.first_name} ${c.last_name || ""}`.trim(),
              })),
            ]}
          />
          <FilterSelect
            testId="filter-severity"
            label="Severity"
            value={filters.severity}
            onChange={(v) => setFilters((f) => ({ ...f, severity: v }))}
            options={[
              { value: "", label: "Any severity" },
              ...SEVERITIES.map((s) => ({
                value: s,
                label: s.charAt(0).toUpperCase() + s.slice(1),
              })),
            ]}
          />
          <FilterSelect
            testId="filter-status"
            label="Status"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={[
              { value: "", label: "Any status" },
              ...STATUSES.map((s) => ({
                value: s,
                label: s.charAt(0).toUpperCase() + s.slice(1),
              })),
            ]}
          />
          <FilterDate
            testId="filter-date-from"
            label="From"
            value={filters.dateFrom}
            onChange={(v) => setFilters((f) => ({ ...f, dateFrom: v }))}
          />
          <FilterDate
            testId="filter-date-to"
            label="To"
            value={filters.dateTo}
            onChange={(v) => setFilters((f) => ({ ...f, dateTo: v }))}
          />
        </div>
      </section>

      {/* Table */}
      <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div
            data-testid="admin-incidents-empty"
            className="px-6 py-16 text-center"
          >
            <p className="font-heading text-base font-semibold text-neutral-900">
              No incidents match these filters
            </p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
              Incidents are filed by teachers from the classroom roster
              (Roster → child → "Incident"). They appear here for admin review,
              acknowledgement, and resolution.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              data-testid="admin-incidents-table"
              className="w-full border-collapse text-left text-sm"
            >
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <Th>Occurred</Th>
                  <Th>Child</Th>
                  <Th>Classroom</Th>
                  <Th>Type</Th>
                  <Th>Severity</Th>
                  <Th>Status</Th>
                  <Th>Acknowledged</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const child = r.child || getChildById(r.child_id);
                  const classroom =
                    r.classroom ||
                    classrooms.find((c) => c.id === r.classroom_id);
                  return (
                    <tr
                      key={r.id}
                      data-testid={`admin-incident-row-${r.id}`}
                      className="border-b border-neutral-100 transition-colors hover:bg-neutral-50"
                    >
                      <Td>{formatIncidentDateTime(r.occurred_at)}</Td>
                      <Td className="font-medium text-neutral-900">
                        {child
                          ? `${child.first_name} ${child.last_name || ""}`.trim()
                          : "—"}
                      </Td>
                      <Td>{classroom?.name || "—"}</Td>
                      <Td>
                        <IncidentTypeBadge type={r.incident_type} />
                      </Td>
                      <Td>
                        <SeverityBadge severity={r.severity} />
                      </Td>
                      <Td>
                        <StatusBadge status={r.status} />
                      </Td>
                      <Td>
                        {r.acknowledged_at ? (
                          <span className="text-xs text-neutral-600">
                            {formatIncidentDateTime(r.acknowledged_at)}
                          </span>
                        ) : (
                          <span className="text-xs text-[#C6851E]">Pending</span>
                        )}
                      </Td>
                      <Td className="text-right">
                        <Link
                          to={`/admin/incidents/${r.id}`}
                          data-testid={`admin-incident-view-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-800 hover:bg-neutral-200"
                        >
                          View
                        </Link>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border bg-white p-5 ${accent ? "border-[#C6851E]/30" : "border-neutral-200"}`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <p
        className={`mt-1 font-heading text-3xl font-semibold tracking-tight ${accent ? "text-[#C6851E]" : "text-neutral-900"}`}
      >
        {value}
      </p>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-3 text-sm text-neutral-700 ${className}`}>
      {children}
    </td>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterDate({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <input
        data-testid={testId}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}
